import { ChangeEvent, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { TableDropdown } from "components/dropdowns/TableDropdown"
import { ModalProviderGeo } from "components/modals/ModalProviderGeo"
import { SearchInput } from "components/search/SearchInput"
import { ProviderTable } from "components/tables/ProviderTable"

import { usePermissions } from "contexts/PermissionsContext"

import { providersService } from "services/providers/providers.service"
import { Provider, ProviderGeoRestriction } from "services/providers/type"

import { PERMISSIONS } from "../../../constants/permissions"

const PAGE_SIZE_CHOICES = [50, 25, 10]
const DEFAULT_PAGE_SIZE = PAGE_SIZE_CHOICES[0]

export function ProvidersTab() {
  const [rows, setRows] = useState<Provider[]>([])
  const [restrictions, setRestrictions] = useState<ProviderGeoRestriction[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(1)
  const [reloadToken, setReloadToken] = useState(0)

  const [geoProvider, setGeoProvider] = useState<Provider | null>(null)

  const { has } = usePermissions()
  const canManageFees = has(PERMISSIONS.PROVIDER_FEES_EDIT)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [data, restr] = await Promise.all([
          providersService.getProviders({ page, pageSize, search }),
          providersService.getRestrictions()
        ])

        if (!data) return

        setPage(data.page)
        setRows(data.providers)
        setTotalPages(data.total_pages)
        setRestrictions(restr)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [page, pageSize, search, reloadToken])

  const handleExportCsv = async () => {
    if (!canManageFees) return
    await providersService.exportFeesCsv()
  }

  const handleImportCsv = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!canManageFees) return

    const input = event.target
    const file = input.files?.[0]
    if (!file) {
      input.value = ""
      return
    }

    const ok = await providersService.importFeesCsv(file)

    input.value = ""

    if (ok) {
      setPage(1)
      setReloadToken((prev) => prev + 1)
    }
  }

  const onToggle = async (row: Provider) => {
    const next = !row.enabled
    setRows((prev) => prev.map((r) => (r.identifier === row.identifier ? { ...r, enabled: next } : r)))

    const ok = await providersService.toggleEnabled(row.identifier, next)
    if (ok) {
      toast.success(`${row.title} ${next ? "enabled" : "disabled"}`)
    } else {
      setRows((prev) => prev.map((r) => (r.identifier === row.identifier ? { ...r, enabled: !next } : r)))
      toast.error("Update failed")
    }
  }

  const onChangeFeeRate = async (row: Provider, nextPercent: number) => {
    if (!canManageFees) return

    if (Number.isNaN(nextPercent) || nextPercent < 0) {
      toast.error("Fee rate must be a non-negative number")
      return
    }

    const nextRate = nextPercent / 100

    // D02/F18: setting an ORIGINALS provider to 0% EXCLUDES it from the effective GGR snapshot
    // entirely (not just from fees) — confirm before sending such a consequential change.
    const isOriginals = (row.provider ?? row.identifier ?? "").toUpperCase() === "ORIGINALS"
    if (isOriginals && nextRate === 0) {
      const confirmed = window.confirm(
        `Setting ${row.title} (ORIGINALS) to 0% will EXCLUDE it from the effective GGR snapshot entirely, ` +
          `not just from fees. Continue?`
      )
      if (!confirmed) {
        // Revert the optimistic input back to the stored value.
        setRows((prev) => prev.map((r) => (r.identifier === row.identifier ? { ...r, feeRate: row.feeRate } : r)))
        return
      }
    }

    setRows((prev) => prev.map((r) => (r.identifier === row.identifier ? { ...r, feeRate: nextRate } : r)))

    const result = await providersService.updateFeeRate(row.identifier, nextRate)
    if (!result) {
      setRows((prev) => prev.map((r) => (r.identifier === row.identifier ? { ...r, feeRate: row.feeRate } : r)))
      return
    }

    // D02/F18: confirm with the authoritative previous value, note whether it synced to the service,
    // and surface any backend warning (e.g. ORIGINALS→0% GGR-exclusion).
    const previousPercent = typeof result.previous_fee_rate === "number" ? `${(result.previous_fee_rate * 100).toFixed(2)}%` : "—"
    toast.success(`Updated fee rate for ${row.title} (was ${previousPercent}${result.nux_synced ? ", synced to service" : ""})`)
    if (result.warning) toast.warn(result.warning)
  }

  const onGeoSaved = (restriction: ProviderGeoRestriction | null) => {
    if (!geoProvider) return
    const providerId = geoProvider.identifier

    setRestrictions((prev) => {
      const without = prev.filter((r) => r.provider_id !== providerId)
      return restriction ? [...without, restriction] : without
    })

    setGeoProvider(null)
  }

  if (loading) return <p className="py-8 text-center text-[rgba(6,7,21,1)] dark:text-white">Loading…</p>

  return (
    <div className="flex flex-col gap-4 overflow-x-auto">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="w-60">
          <SearchInput placeholder="Search providers…" onChange={setSearch} />
        </div>

        <div className="flex flex-col-reverse md:flex-row gap-4 items-start md:items-center">
          {canManageFees && (
            <div className="flex gap-4 items-center">
              <button
                type="button"
                className="btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06]"
                onClick={handleExportCsv}
              >
                Export fees CSV
              </button>

              <label className="btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06]">
                Import fees CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportCsv} />
              </label>
            </div>
          )}

          <TableDropdown
            value={pageSize}
            onValueChange={(v: number) => {
              setPageSize(v)
              setPage(1)
            }}
            columns={PAGE_SIZE_CHOICES}
          />
        </div>
      </div>

      <ProviderTable
        rows={rows}
        restrictions={restrictions}
        onToggle={onToggle}
        onChangeFeeRate={onChangeFeeRate}
        onOpenGeo={setGeoProvider}
        page={page}
        totalPages={totalPages}
        setPage={setPage}
        canManageFees={canManageFees}
      />

      <ModalProviderGeo
        open={!!geoProvider}
        provider={geoProvider}
        existing={restrictions.find((r) => r.provider_id === geoProvider?.identifier) ?? null}
        onClose={() => setGeoProvider(null)}
        onSaved={onGeoSaved}
      />
    </div>
  )
}
